/**
 * Visual shell for the addin — header on top, bottom 5-tab nav. Ported
 * from the new design spec. Existing feature screens slot into tabs
 * unchanged; this file only owns the chrome.
 */
import { ReactNode, useEffect, useState } from 'react';
import {
  Home,
  BookOpen,
  FileText,
  ClipboardList,
  Users,
} from 'lucide-react';

export type TabKey =
  | 'home'
  | 'summary'
  | 'obligations'
  | 'collaboration'
  | 'clauses';

const NAV: Array<{ key: TabKey; label: string; Icon: typeof Home }> = [
  { key: 'home', label: 'Home', Icon: Home },
  { key: 'summary', label: 'Summary', Icon: FileText },
  { key: 'obligations', label: 'Obligations', Icon: ClipboardList },
  { key: 'collaboration', label: 'Collaborate', Icon: Users },
  { key: 'clauses', label: 'Clauses', Icon: BookOpen },
];

interface AppShellProps {
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
  contractTitle?: string | null;
  contractStatus?: string | null;
  counterparty?: string | null;
  reviewBadge?: number;
  /** True when the open WOPI file has been modified since the current
   * edit lock was acquired. Renders an "Unsaved changes" pill next to
   * the contract title. */
  isDirty?: boolean;
  children: ReactNode;
  /** Optional override of the body inset (set to 0 when the inner screen
   * wants full bleed, e.g. Assistant chat). */
  flush?: boolean;
}

export function AppShell({
  activeTab,
  onTabChange,
  contractTitle,
  contractStatus,
  counterparty,
  reviewBadge,
  isDirty,
  children,
  flush = false,
}: AppShellProps) {
  return (
    <div
      className="flex flex-col h-screen bg-[var(--bg-soft)]"
      style={{ fontFamily: 'var(--font-ui)' }}
    >
      {/* Header */}
      <header
        className="flex-shrink-0 bg-white border-b border-[var(--line)]"
        style={{ boxShadow: 'var(--shadow-1)' }}
      >
        {/* Brand row */}
        <div className="flex items-center px-4 py-3">
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Ezylex.ai"
              className="h-6 w-auto"
              style={{ maxWidth: 150 }}
            />
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: 'var(--brand-tint)', color: 'var(--brand-3)' }}
            >
              CLM
            </span>
          </div>
        </div>

        {/* Contract row */}
        {contractTitle && (
          <div className="flex items-center gap-2 px-4 pb-3 -mt-1">
            <div
              className="w-8 h-8 rounded-md grid place-items-center flex-shrink-0"
              style={{ background: 'var(--bg-soft)' }}
            >
              <FileText size={15} className="text-[var(--ink-3)]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate text-[var(--ink)] flex items-center gap-1.5">
                <span className="truncate">{contractTitle}</span>
                {isDirty && (
                  <span
                    title="The document has been modified since editing began. Save & Leave to promote as a new version."
                    className="text-[9.5px] font-bold uppercase tracking-wider whitespace-nowrap rounded px-1.5 py-0.5 border"
                    style={{
                      background: '#FFF7E6',
                      color: '#8B5A00',
                      borderColor: '#F5C879',
                    }}
                  >
                    Unsaved
                  </span>
                )}
              </div>
              <div className="text-[11px] text-[var(--ink-3)] flex items-center gap-1.5 mt-0.5">
                {contractStatus && (
                  <span
                    className="sev"
                    style={{
                      background: 'var(--bg-soft-2)',
                      color: 'var(--ink-2)',
                      fontSize: 9.5,
                      padding: '2px 6px',
                    }}
                  >
                    {contractStatus}
                  </span>
                )}
                {counterparty && <span className="truncate">{counterparty}</span>}
              </div>
            </div>
            {/* Switch contract button hidden until we ship a proper in-pane
                switcher. Opening the CLM contracts list in a new tab was the
                interim behavior; user prefers it hidden for now. */}
          </div>
        )}
      </header>

      {/* Body */}
      <main
        className={
          'flex-1 min-h-0 ' +
          (flush ? 'flex flex-col overflow-hidden' : 'overflow-y-auto')
        }
        style={{ background: 'var(--bg-soft)' }}
      >
        {flush ? (
          <div className="flex-1 min-h-0 flex flex-col">{children}</div>
        ) : (
          <div className="p-4 space-y-3">{children}</div>
        )}
      </main>

      {/* Bottom nav — HIDDEN for the initial Microsoft AppSource submission to
          keep the reviewed surface small (Home only). Change the `false` below
          back to render this <nav> to bring back the Summary / Obligations /
          Collaborate / Clauses tabs later. */}
      {false && (
        <nav
          className="flex-shrink-0 bg-white border-t border-[var(--line)] flex items-stretch"
          style={{ boxShadow: '0 -1px 2px rgba(15,27,23,.04)' }}
        >
          {NAV.map(({ key, label, Icon }) => {
            const active = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onTabChange(key)}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 relative"
                style={{
                  color: active ? 'var(--brand-3)' : 'var(--ink-3)',
                }}
              >
                <span className="relative">
                  <Icon size={21} strokeWidth={active ? 2.2 : 1.8} />
                  {key === 'review' && reviewBadge ? (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] grid place-items-center rounded-full text-[10px] font-bold text-white px-1"
                      style={{ background: 'var(--high)' }}
                    >
                      {reviewBadge}
                    </span>
                  ) : null}
                </span>
                <span
                  className="text-[10.5px] font-medium"
                  style={{ fontWeight: active ? 700 : 500 }}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}

/** Convenience hook: keep tab state in localStorage so we restore on
 * reload (Office reloads the iframe on certain events). */
export function usePersistedTab(initial: TabKey = 'home'): [TabKey, (t: TabKey) => void] {
  const [tab, setTabState] = useState<TabKey>(() => {
    try {
      const stored = localStorage.getItem('ezylex-shell-tab') as TabKey | null;
      const allowed: TabKey[] = ['home', 'summary', 'obligations', 'collaboration', 'clauses'];
      // Migrate any legacy tab value (more, assistant, review, risk) to a
      // sensible new tab so users don't land on a dead screen.
      if (stored === ('more' as TabKey)) return 'clauses';
      if (stored === ('assistant' as TabKey)) return 'home';
      if (stored === ('review' as TabKey)) return 'home';
      if (stored === ('risk' as TabKey)) return 'home';
      return stored && allowed.includes(stored) ? stored : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('ezylex-shell-tab', tab);
    } catch {}
  }, [tab]);
  return [tab, setTabState];
}
